import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import { CrewList, TableColumn, CrewChange } from '../../types';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useCrewChanges } from '../../hooks/useCrewChanges';
import Button from '../../components/Form/Button';
import QuickCrewList from '../../components/HR/QuickCrewList';
import { exportToExcel } from '../../utils/exportToExcel';
import { resolveCrewStint } from '../../utils/crewStint';
import { PageHeader, FilterBar, StatsCards, FilterConfig } from '../../components/Common';
import VesselInfoForm from './VesselInfoForm';
import VacationModal, { VacationEmployee } from './VacationModal';
import { openCrewListPrintWindow } from './CrewListPrintTemplate';

const CrewListManagement: React.FC = () => {
  const { data: employees = [] } = useEmployees({ populate: '*' });
  const { data: branches = [] } = useBranches({ populate: '*' });
  const [showQuickList, setShowQuickList] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [rankFilter, setRankFilter] = useState<string>('all');
  const { data: crewChangesData = [] } = useCrewChanges({ pagination: { pageSize: 1000 } });
  const crewChanges = crewChangesData as CrewChange[];

  const [activeCrewList, setActiveCrewList] = useState<CrewList[]>([]);
  const [vesselName, setVesselName] = useState('');
  const [callSign, setCallSign] = useState('');
  const [imoNumber, setImoNumber] = useState('');
  const [vesselLocation, setVesselLocation] = useState('');
  const [vesselFlag, setVesselFlag] = useState('Egypt');
  const [showVacationModal, setShowVacationModal] = useState(false);
  
  const vacationEmployees: VacationEmployee[] = useMemo(() => {
    return (employees as any[])
      .filter((emp: any) => emp.status === 'on_leave' || emp.status === 'vacation')
      .map((emp: any) => ({
        id: emp.documentId || emp.id,
        name: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
        position: emp.rank || emp.position || '-',
        rank: emp.rank || '-',
        vacationStart: emp.leaveStartDate || '',
        vacationEnd: emp.leaveEndDate || '',
        vacationType: emp.leaveType || 'Leave',
        branchId: emp.branchId || ''
      }));
  }, [employees]);

  const getEmployeeName = useCallback((employeeId: string) => {
    const employee = employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
    return employee ? employee.name : '-';
  }, [employees]);

  const getEmployeeRank = useCallback((employeeId: string) => {
    const employee = employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
    return employee ? (employee.rank || employee.position || '-') : '-';
  }, [employees]);

  const getEmployeeNationality = useCallback((employeeId: string) => {
    const employee = employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
    return employee ? employee.nationality || '-' : '-';
  }, [employees]);

  const getEmployeeBirthDate = useCallback((employeeId: string) => {
    const employee = employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
    return employee?.birthDate ? new Date(employee.birthDate).toLocaleDateString() : '-';
  }, [employees]);

  const getEmployeeSeamanBookNumber = useCallback((employeeId: string) => {
    const employee = employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
    return employee ? employee.seamanBookNumber || '-' : '-';
  }, [employees]);

  const getEmployeeSeamanBookExpiry = useCallback((employeeId: string) => {
    const employee = employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
    return employee?.seamanBookExpiry ? new Date(employee.seamanBookExpiry).toLocaleDateString() : '-';
  }, [employees]);

  const getBranchName = useCallback((branchId: string) => {
    const branch = branches.find((b: any) => b.id?.toString() === branchId || b.documentId === branchId);
    return branch ? branch.name : '-';
  }, [branches]);

  const getBranchInfo = useCallback((branchId: string) => {
    const branch = branches.find((b: any) => b.id?.toString() === branchId || b.documentId === branchId);
    if (!branch) return '-';
    if (branch.type === 'vessel') return `${branch.name} (Vessel)`;
    return branch.name;
  }, [branches]);

  const generateActiveCrewList = useCallback(() => {
    const byEmployee: { [key: string]: CrewChange[] } = {};
    crewChanges.forEach(change => {
      (byEmployee[change.employeeId!] ||= []).push(change);
    });
    // Active = the employee's latest crew change is Sign On or Continue.
    // joinDate = the sign_on that began the current uninterrupted stay — a
    // `continue` must not reset it. lastUpdate tracks the latest change date.
    const crewList: CrewList[] = Object.entries(byEmployee).flatMap(([employeeId, changes]) => {
      const stint = resolveCrewStint(changes);
      if (!stint.aboard || !stint.latest) return [];
      return [{
        id: `crew_${employeeId}`,
        employeeId,
        joinDate: stint.joinDate!,
        lastUpdate: stint.lastUpdate || undefined,
        branchId: stint.latest.branchId!,
        status: 'active' as const,
      }];
    });
    setActiveCrewList(crewList);
  }, [crewChanges]);

  useEffect(() => {
    generateActiveCrewList();
  }, [generateActiveCrewList]);

  const uniqueBranches = Array.from(new Set(activeCrewList.map(crew => crew.branchId).filter(Boolean)));
  const uniqueRanks = Array.from(new Set(activeCrewList.map(crew => getEmployeeRank(crew.employeeId)).filter(Boolean)));

  const filteredActiveCrewList = useMemo(() => {
    return activeCrewList.filter(crew => {
      const employeeName = getEmployeeName(crew.employeeId);
      const branchName = getBranchInfo(crew.branchId);
      const employeeRank = getEmployeeRank(crew.employeeId);
      const matchesSearch = !searchTerm || (
        employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employeeRank.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || crew.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || crew.branchId === branchFilter;
      const matchesRank = rankFilter === 'all' || employeeRank === rankFilter;
      return matchesSearch && matchesStatus && matchesBranch && matchesRank;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCrewList, searchTerm, statusFilter, branchFilter, rankFilter]);

  const handleExport = () => {
    const exportData = filteredActiveCrewList.map(crew => ({
      'Employee Name': getEmployeeName(crew.employeeId),
      'Rank': getEmployeeRank(crew.employeeId),
      'Vessel/Headquarters': getBranchInfo(crew.branchId),
      'Nationality': getEmployeeNationality(crew.employeeId),
      'Date of Birth': getEmployeeBirthDate(crew.employeeId),
      'Seaman Book No.': getEmployeeSeamanBookNumber(crew.employeeId),
      'Seaman Book Expiry': getEmployeeSeamanBookExpiry(crew.employeeId),
      'Join Date': new Date(crew.joinDate).toLocaleDateString(),
      'Last Update': crew.lastUpdate ? new Date(crew.lastUpdate).toLocaleDateString() : '-',
      'Status': crew.status.toUpperCase()
    }));
    exportToExcel(exportData, `active_crew_list_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const vesselInfo = {
      vesselName: vesselName || '...............',
      location: vesselLocation || '...............',
      callSign: callSign || '...............',
      imoNumber: imoNumber || '...............',
      flag: vesselFlag || '...............',
      date: new Date().toLocaleDateString()
    };
    const crewData = filteredActiveCrewList.map(crew => ({
      name: getEmployeeName(crew.employeeId),
      rank: getEmployeeRank(crew.employeeId),
      nationality: getEmployeeNationality(crew.employeeId),
      dateOfBirth: getEmployeeBirthDate(crew.employeeId),
      seamanBookNo: getEmployeeSeamanBookNumber(crew.employeeId),
      seamanBookExpiry: getEmployeeSeamanBookExpiry(crew.employeeId),
      dateOfJoin: new Date(crew.joinDate).toLocaleDateString()
    }));
    openCrewListPrintWindow(vesselInfo, crewData);
  };

  const columns: TableColumn[] = [
    { key: 'employeeId', label: 'Employee Name', sortable: true, render: (_: string, item: CrewList) => getEmployeeName(item.employeeId!) },
    { key: 'employeeId', label: 'Rank', sortable: true, render: (_: string, item: CrewList) => getEmployeeRank(item.employeeId!) },
    { key: 'branchId', label: 'Vessel/Headquarters', sortable: true, render: (_: string, item: CrewList) => getBranchInfo(item.branchId!) },
    { key: 'employeeId', label: 'Nationality', sortable: true, render: (_: string, item: CrewList) => getEmployeeNationality(item.employeeId!) },
    { key: 'employeeId', label: 'Date of Birth', sortable: true, render: (_: string, item: CrewList) => getEmployeeBirthDate(item.employeeId!) },
    { key: 'employeeId', label: 'Seaman Book No.', sortable: true, render: (_: string, item: CrewList) => getEmployeeSeamanBookNumber(item.employeeId!) },
    { key: 'employeeId', label: 'Seaman Book Expiry', sortable: true, render: (_: string, item: CrewList) => getEmployeeSeamanBookExpiry(item.employeeId!) },
    { key: 'joinDate', label: 'Join Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'lastUpdate', label: 'Last Update', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        {value.toUpperCase()}
      </span>
    )}
  ];

  const stats = [
    { label: 'Total Active Crew', value: activeCrewList.length, color: 'green' as const },
    { label: 'Crew on Vacation', value: vacationEmployees.length, color: 'yellow' as const },
    { label: 'Unique Ranks', value: uniqueRanks.length, color: 'blue' as const },
    { label: 'Last Updated', value: new Date().toLocaleDateString(), color: 'purple' as const },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'status', label: 'Status', type: 'select', value: statusFilter,
      onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Statuses' }, { value: 'active', label: 'Active' }],
    },
    {
      key: 'branch', label: 'Vessel/Headquarters', type: 'select', value: branchFilter,
      onChange: setBranchFilter,
      options: [
        { value: 'all', label: 'All Vessels/HQ' },
        ...uniqueBranches.map(id => ({ value: id, label: getBranchName(id) })),
      ],
    },
    {
      key: 'rank', label: 'Rank', type: 'select', value: rankFilter,
      onChange: setRankFilter,
      options: [
        { value: 'all', label: 'All Ranks' },
        ...uniqueRanks.map(r => ({ value: r, label: r })),
      ],
    },
  ];

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setRankFilter('all');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Active Crew List"
        description="Current crew members who are signed on or continuing their assignments"
        actions={[{ label: 'Refresh List', onClick: generateActiveCrewList, variant: 'primary' }]}
      />

      <div onClick={() => setShowVacationModal(true)} className="cursor-pointer">
        <StatsCards stats={stats} />
      </div>

      <VesselInfoForm
        vesselName={vesselName} callSign={callSign} imoNumber={imoNumber}
        vesselLocation={vesselLocation} vesselFlag={vesselFlag}
        onVesselNameChange={setVesselName} onCallSignChange={setCallSign}
        onImoNumberChange={setImoNumber} onVesselLocationChange={setVesselLocation}
        onVesselFlagChange={setVesselFlag}
      />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex justify-between items-start">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                This list is automatically generated from the latest crew changes. Only employees with "Sign On" or "Continue" status are shown. 
                The list updates in real-time when crew changes are made. Use the refresh button to ensure you have the latest data.
              </p>
            </div>
          </div>
          <Button type="button" variant="primary" onClick={() => setShowQuickList(true)} className="ml-4 whitespace-nowrap">
            Quick Print List
          </Button>
        </div>
      </div>

      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm}
        searchPlaceholder="Search active crew..."
        filters={filters} onClearFilters={clearFilters}
        totalCount={activeCrewList.length} filteredCount={filteredActiveCrewList.length}
        entityName="Active Crew Members"
        onExport={handleExport} onPrint={handlePrint}
      />

      <Table
        columns={columns} data={filteredActiveCrewList}
        showAddButton={false} showEditButton={false} showDeleteButton={false} searchable={false}
      />

      {showQuickList && <QuickCrewList onClose={() => setShowQuickList(false)} vesselInfo={{
        vesselName: vesselName || '...............',
        location: vesselLocation || '...............',
        callSign: callSign || '...............',
        imoNumber: imoNumber || '...............',
        flag: vesselFlag || '...............',
        date: new Date().toLocaleDateString()
      }} />}
      {showVacationModal && <VacationModal employees={vacationEmployees} onClose={() => setShowVacationModal(false)} />}
    </div>
  );
};

export default CrewListManagement;
